import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { useForm } from "react-hook-form";
import { add } from "date-fns";

import { CSS_COMPONENT_PREFIX, MAX_DATES } from "config";
import t from "lib/i18n";
import { getMinPostalDate } from "lib/favourites";
import { getFavouritesSearchURL } from "lib/searchUrl";
import { favouritesFormState, favouriteTickets, selectedFavouriteTicket } from "state";
import DateTime from "models/DateTime";
import useConfig from 'hooks/useConfig';
import useLayout from "hooks/useLayout";
import useGTM, { useGTMChange } from "hooks/useGTM";
import {
  trackFavouritesDateChange,
  trackFavouritesDeliveryChange,
  trackFavouritesSubmitDeliveryOptions,
  trackFavouritesSubmitPaymentDetails,
} from "lib/tracking";
import "styles/components/favourites/form.scss";

import FavouriteTicket from "components/favourites/FavouriteTicket";
import TicketSelection from "components/favourites/TicketSelection";
import DatesSelector from "components/datetime/DatesSelector";
import DeliveryOption from "components/favourites/DeliveryOption";
import Button, { ButtonTypes } from "components/ui/Button";
import BookingFees from "components/BookingFees";

const FavouritesForm = ({ onGetValues }) => {
  const {
    redirectUrl,
    mode,
    searchCallBack,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
  } = useConfig();
  const { isDesktop } = useLayout();
  const tickets = useRecoilValue(favouriteTickets);
  const selectedTicket = useRecoilValue(selectedFavouriteTicket);
  const { isSeason, delivery } = selectedTicket;
  const hasMultipleTickets = tickets?.length > 1;
  const minPostalDate = delivery ? getMinPostalDate(delivery) : null;

  const form = useRecoilValue(favouritesFormState);
  const methods = useForm({
    defaultValues: form ?? {
      reorder: null,
      startDate: new DateTime().departNow(),
    },
  });
  const {
    errors,
    handleSubmit,
    getValues,
    register,
    setValue,
    watch,
  } = methods;

  const { startDate } = watch(["startDate"]);

  useEffect(() => {
    register({ name: "reorder" }, { required: true });
    register({ name: "startDate" }, { required: true });
    onGetValues(getValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGTM = useGTM();
  useGTMChange(startDate, trackFavouritesDateChange);

  useEffect(() => {
    setValue("reorder", selectedTicket.id, true);
    onChangeStartDate({
      dateTime: minPostalDate
        ? new DateTime(minPostalDate)
        : new DateTime().departNow()
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket]);

  function onChangeStartDate (newValue) {
    setValue("startDate", startDate?.produce(newValue), true);
  }

  function onChangeDeliveryOption (event) {
    event.preventDefault();
    dispatchGTM(trackFavouritesDeliveryChange());
    handleSubmit(data => onSubmit(data, false))();
  }

  function onFormSubmit (event) {
    event.preventDefault();
    if (delivery) {
      dispatchGTM(trackFavouritesSubmitPaymentDetails());
    } else {
      dispatchGTM(trackFavouritesSubmitDeliveryOptions());
    }
    handleSubmit(onSubmit)();
  }

  function onSubmit (data, useDeliveryPreference = true) {
    const urlSearch = getFavouritesSearchURL({
      data,
      useDeliveryPreference,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    });

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  }

  const minDate = minPostalDate || undefined;
  const maxDate = add(new Date(), MAX_DATES[isSeason ? 'SEASON' : 'SINGLES']);

  const baseClass = CSS_COMPONENT_PREFIX + 'favourites-form';

  return (
    <form
      className={baseClass}
      onSubmit={onFormSubmit}
    >
      <div className={`${baseClass}__selected-ticket`}>
        <FavouriteTicket
          ticket={selectedTicket}
          collapsible={isDesktop}
        />
      </div>
      {hasMultipleTickets && <TicketSelection />}
      <DatesSelector
        disableTimePicker={true}
        errors={errors}
        fromDateTime={startDate}
        isSeason={isSeason}
        minDate={minDate}
        maxDate={maxDate}
        onChangeFromDateTime={onChangeStartDate}
        rightAlignPopup={!delivery}
        ticketType="single"
      />
      {delivery && (
        <DeliveryOption
          delivery={delivery}
          onChange={onChangeDeliveryOption}
        />
      )}
      <Button
        className={`${baseClass}__submit`}
        text={t(`favourites.form.submit.${delivery ? 'payment' : 'delivery'}`)}
        type={ButtonTypes.SUBMIT}
      />
      <BookingFees />
    </form>
  );
};

export default FavouritesForm;
